import React from "react";
import { motion } from "framer-motion";
import { Overline } from "./Motifs";
import { MUSIC } from "../data/content";

// Pull portrait from MUSIC.storyWorldImage's neighbour (we kept it in MUSIC for convenience).
// We expose it via a constant so the structure stays clean.
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/fwfdua8d_divya%20fun.png";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-2 bg-[#D95D39] rounded-lg -rotate-2" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg border border-[#E5DFD3] bg-[#F3EDE4]">
                <img
                  src={PORTRAIT}
                  alt="Divya Ramachandran"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 text-xs tracking-[0.22em] uppercase text-[#5C6A8F]">
                Divya Ramachandran — Bangalore
              </p>
            </div>
          </motion.div>

          {/* Bio */}
          <div className="lg:col-span-7">
            <Overline>About</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1E2A4F] mt-5 leading-[1] tracking-tight">
              A Bangalore-based author finding the seams between
              <em className="italic font-light"> stories, play, and learning.</em>
            </h2>

            <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-6 text-[#5C6A8F] leading-relaxed text-[15px]">
              <p>
                Dr. Divya Ramachandran is a Bangalore-based children&apos;s author, educator,
                singer, artist, and storyteller. As a multipassionate, she has spent over 15
                years working in education, curriculum design, and creative learning spaces.
                A Master&apos;s in Design Thinking from <em>Domus Academy, Milan</em> shaped
                the way she builds learning experiences.
              </p>
              <p>
                Through her books, workshops, and performances, she introduces children to
                art, culture, imagination, and the joy of playful exploration. A TEDx speaker
                and featured author at the Bangalore Literature Festival, she has worked with
                <em> Scholastic</em>, <em>Disney</em>, BYJU&apos;S, Embibe, and schools across
                India. Her doctoral work at SSBM Geneva explored how AR technology can
                transform classroom experiences for special-needs learners.
              </p>
            </div>

            {/* Facts */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#E5DFD3] pt-8">
              {[
                { k: "Based in", v: "Bangalore" },
                { k: "Doctorate", v: "SSBM Geneva, 2025" },
                { k: "Working since", v: "2010" },
                { k: "Currently", v: "Writing Book Three" },
              ].map((f) => (
                <div key={f.k}>
                  <Overline>{f.k}</Overline>
                  <p className="font-display text-xl text-[#1E2A4F] mt-2">{f.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Story world map banner */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-20 grid lg:grid-cols-12 gap-8 items-center"
          data-testid="about-story-world"
        >
          <div className="lg:col-span-5 lg:order-2">
            <Overline>The Ecosystem</Overline>
            <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-4 leading-tight">
              Welcome to <em className="italic font-light">Divya&apos;s Story World</em>.
            </h3>
            <p className="text-[#5C6A8F] mt-4 leading-relaxed">
              Books, podcasts, and small adventures — all in one place. A living map of the
              stories Meera, Mr. Foxx, and a thousand other characters are waiting to share.
            </p>
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <div className="relative rounded-lg overflow-hidden border border-[#E5DFD3]">
              <img
                src={MUSIC.storyWorldImage}
                alt="Welcome to Divya's Story World — a map of all the books and the Magic Ink podcast"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
